import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { getReports, deleteReport } from '../../services/reports';

const ViewReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getReports();
      if (response.success) {
        setReports(response.reports);
      } else {
        setError("Failed to load reports");
      }
    } catch (err) {
      console.error("Failed to load reports", err);
      setError("Failed to load reports. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await deleteReport(reportToDelete.id);
      if (response.success) {
        setReports(reports.filter(r => r.id !== reportToDelete.id));
        setDeleteModalOpen(false);
        setReportToDelete(null);
      }
    } catch (err) {
      console.error("Failed to delete report", err);
      setError("Failed to delete report. Please try again later.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const getReportTypeIcon = (type) => {
    switch(type) {
      case 'statistical': return 'chart-bar';
      case 'summary': return 'file-alt';
      case 'analysis': return 'microscope';
      case 'dashboard': return 'tachometer-alt';
      default: return 'file-medical-alt';
    }
  };

  // Apply filters to reports
  const filteredReports = reports.filter(report => {
    // Filter by type
    if (filter !== 'all' && report.report_type !== filter) {
      return false;
    }
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      return (
        report.title.toLowerCase().includes(query) || 
        (report.description && report.description.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  return (
    <ResearchLayout pageTitle="View Reports">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-2">Research Reports</h3>
                    <p className="mb-0 opacity-75">
                      View, edit and manage your saved research reports
                    </p>
                  </div>
                  <Link to="/statistician/reports" className="btn btn-light">
                    <i className="fa fa-plus me-2"></i>
                    Create Report
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and filter options */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa fa-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search reports..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <select 
                      className="form-select"
                      value={filter}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Report Types</option>
                      <option value="statistical">Statistical Analysis</option>
                      <option value="summary">Summary Reports</option>
                      <option value="analysis">Detailed Analysis</option>
                      <option value="dashboard">Dashboard Reports</option>
                    </select>
                  </div>
                  <div className="col-md-5 text-md-end">
                    <span className="text-muted">
                      {filteredReports.length} {filteredReports.length === 1 ? 'report' : 'reports'} found
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading reports...</p>
          </div>
        ) : filteredReports.length > 0 ? (
          <div className="row">
            {filteredReports.map(report => (
              <div key={report.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className="report-icon me-2">
                        <i className={`fa fa-${getReportTypeIcon(report.report_type)} text-info`}></i>
                      </div>
                      <h6 className="mb-0">{report.title}</h6>
                    </div>
                    <div className="dropdown">
                      <button 
                        className="btn btn-sm btn-icon" 
                        type="button" 
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="fa fa-ellipsis-v"></i>
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                          <Link to={`/statistician/reports/${report.id}`} className="dropdown-item">
                            <i className="fa fa-eye me-2"></i>
                            View
                          </Link>
                        </li>
                        <li>
                          <Link to={`/statistician/reports/edit/${report.id}`} className="dropdown-item">
                            <i className="fa fa-edit me-2"></i>
                            Edit
                          </Link>
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button 
                            className="dropdown-item text-danger" 
                            onClick={() => handleDeleteClick(report)}
                          >
                            <i className="fa fa-trash-alt me-2"></i>
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-info me-2">
                          {report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}
                        </span>
                        <span className="text-muted small">
                          {new Date(report.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-muted small mb-0">
                        {report.description ? 
                          (report.description.length > 100 ? 
                            `${report.description.substring(0, 100)}...` : 
                            report.description) : 
                          'No description provided'}
                      </p>
                    </div>
                    <div className="text-end">
                      <Link to={`/statistician/reports/${report.id}`} className="btn btn-sm btn-outline-info">
                        View Report
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-file-alt fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Reports Found</h5>
            <p className="text-muted">
              {filter !== 'all' || searchQuery ? 
                'Try changing your search criteria or create a new report.' : 
                'You haven\'t created any reports yet.'}
            </p>
            <Link to="/statistician/reports" className="btn btn-info mt-2">
              <i className="fa fa-plus me-2"></i>
              Create New Report
            </Link>
          </div>
        )}
      </div>

      {deleteModalOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Report</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setDeleteModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the report "{reportToDelete?.title}"? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-trash-alt me-2"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResearchLayout>
  );
};

export default ViewReportsPage;